"use client";

import { useRef, useState } from "react";
import type { Column } from "@tanstack/react-table";
import { Filter } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Popover } from "../Popover";
import { TextColumnFilterField } from "./TextColumnFilterField";
import { NumberRangeColumnFilterField } from "./NumberRangeColumnFilterField";
import { CheckboxColumnFilterField } from "./CheckboxColumnFilterField";
import { DateRangeColumnFilterField } from "./DateRangeColumnFilterField";
import { useColumnFilterDraft } from "./hooks/useColumnFilterDraft";
import type { NumberRangeValue } from "../NumberRangeInput";
import type { DateRangeValue } from "../DateRangeInput";
import { Button } from "../Button";

interface TableColumnFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
}

export function TableColumnFilter<TData, TValue>({
  column,
}: TableColumnFilterProps<TData, TValue>) {
  const filter = column.columnDef.meta?.filter;
  const { draft, setDraft, isActive, clear } = useColumnFilterDraft(
    column,
    filter,
  );

  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!filter) return null;

  const handleClear = () => {
    clear();
    setOpen(false);
  };

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Button
        ref={buttonRef}
        type="button"
        variant="ghost"
        size="sm"
        data-testid={`filter-button-${column.id}`}
        icon={({ className }) => (
          <Filter
            className={twMerge(className, "size-3")}
            fill={isActive ? "#1884F9" : "transparent"}
          />
        )}
        onClick={() => setOpen((prev) => !prev)}
        className={twMerge(
          "rounded-xs bg-transparent p-[3px] hover:bg-transparent",
          isActive ? "text-brand-blue" : "hover:text-brand-blue text-[#8A94A6]",
        )}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={buttonRef}
        placement="bottom"
        offset={20}
        className="overflow-hidden rounded-lg border border-[#EDEFF2] bg-white px-3 py-2 shadow-[0px_12px_28px_0px_#10182824,0px_2px_8px_0px_#1018280F]"
      >
        {filter.type === "text" && (
          <TextColumnFilterField
            value={draft as string}
            placeholder={filter.placeholder}
            onChange={setDraft}
          />
        )}
        {filter.type === "numberRange" && (
          <NumberRangeColumnFilterField
            value={draft as NumberRangeValue}
            min={filter.min}
            minPlaceholder={filter.minPlaceholder}
            maxPlaceholder={filter.maxPlaceholder}
            onChange={setDraft}
          />
        )}
        {filter.type === "dateRange" && (
          <DateRangeColumnFilterField
            value={draft as DateRangeValue}
            onChange={setDraft}
          />
        )}
        {filter.type === "checkbox" && (
          <CheckboxColumnFilterField
            value={draft as string[]}
            options={filter.options}
            onChange={setDraft}
          />
        )}
        {isActive && (
          <>
            <div className="bg-brand-gray mb-2 h-px" />
            <Button
              color="danger"
              variant="ghost"
              className="h-[34px] w-full font-semibold"
              data-testid={`filter-clear-${column.id}`}
              onClick={handleClear}
            >
              Clear filter
            </Button>
          </>
        )}
      </Popover>
    </span>
  );
}
